import { DialogModule, DialogRef } from "@angular/cdk/dialog";
import { CommonModule } from "@angular/common";
import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
} from "@angular/core";
import { FormControl, ReactiveFormsModule } from "@angular/forms";
import { UntilDestroy, untilDestroyed } from "@ngneat/until-destroy";
import { getKey, TestId } from "pax-ui/core";
import { Observable } from "rxjs";

import { DialogComponent } from "../dialog";
import { FormEvent } from "../form-factory/form-factory";

import { FormFieldLabelComponent } from "./components/form-field-label/form-field-label";
import { FormFieldInputDirective } from "./form-field-input-directive";
import { FormFieldModalService } from "./form-field-modal.service";
import { componentLookup } from "./input-component-lookup";
import { InputBase } from "./inputs/input-base";
import { FocusChangedEvent } from "./inputs/input-typeahead";
import { FormField } from "./model/base";
import { HelpDialog } from "./model/dialog/form-dialog";
import { ValidationErrorKey } from "./validators/general-validation.";

@UntilDestroy()
@Component({
  imports: [
    CommonModule,
    ReactiveFormsModule,
    FormFieldInputDirective,
    DialogModule,
    FormFieldLabelComponent,
  ],
  selector: "pax-form-field",
  templateUrl: "./form-field.html",
  styleUrls: ["./form-field.scss"],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FormFieldComponent implements OnInit, OnDestroy {
  @Input()
  field!: FormField;

  @Input()
  control!: FormControl;

  @Input()
  notifications$!: Observable<FormEvent | null>;

  /**
   * Optional property which InputArray uses in order to generate unique ids for form components.
   */
  @Input()
  htmlIdPrefix?: string | undefined;

  @Output()
  focusChange = new EventEmitter<FocusChangedEvent>();

  @ViewChild(FormFieldInputDirective, { static: true })
  paxFormFieldInput!: FormFieldInputDirective;

  errorMessagesMap: Record<string, string> = {};
  dialogRef: DialogRef<DialogComponent<HelpDialog>> | undefined;

  errorMessageTestId = TestId.FORM_FIELD_ERROR_MESSAGE;

  constructor(private readonly formFieldModalService: FormFieldModalService) {}

  ngOnInit() {
    void this.loadInputComponent();
  }

  ngOnDestroy() {
    if (this.dialogRef) {
      this.dialogRef.close();
    }
  }

  localizedErrorMessages = (error: Partial<CustomValidationErrors> | null) => {
    if (!error) {
      return null;
    }

    const errorMessages: string[] = [];

    Object.keys(error).forEach((key: string) => {
      // Unfortunately we lose the type of the keys when using Object.keys()
      const typeAssertedKey = key as ValidationErrorKey;
      let errorMessageDefinition = undefined;

      if (this.field.errorMessages) {
        errorMessageDefinition = this.field.errorMessages[typeAssertedKey];
      }

      let message = JSON.stringify(error); // Fallback

      if (errorMessageDefinition) {
        if (typeof errorMessageDefinition === "string") {
          message = errorMessageDefinition;
        } else {
          // Errors with context information. E.g. {"maxlength":{"requiredLength":4,"actualLength":12}}
          const errorMessage = error[typeAssertedKey];

          if (!errorMessage) {
            throw new Error(
              "This error should never occur and means we have a logical error",
            );
          }

          message = errorMessageDefinition(errorMessage);
        }
      }
      errorMessages.push(message);
    });

    return errorMessages.join(" ");
  };

  get shouldDisplayDialogBelowTheLabel() {
    return this.field.helpDialog?.position === "below";
  }

  openDialog() {
    this.dialogRef = this.formFieldModalService.tryOpenHelpDialog(
      this.field.helpDialog,
    );
  }

  async loadInputComponent() {
    const viewContainerRef = this.paxFormFieldInput.viewContainerRef;
    viewContainerRef.clear();

    const component = componentLookup[this.field.controlType];

    if (!component) {
      throw new Error(
        `Was not able to find a form control with the controlType '${this.field.controlType}'`,
      );
    }

    const loadedComponent = await component.loader();
    const componentClass = loadedComponent.default;

    // InputDefault type below is technically not correct, since the exact component is being looked up based on control type. We assume all input components share the same two basic properties (form and field).
    const componentRef =
      // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
      viewContainerRef.createComponent<InputBase>(componentClass);

    // Setting input properties this way triggers the 'ngOnChange' and other lifecycle hooks correctly
    componentRef.setInput(getKey<InputBase>("field"), this.field);
    componentRef.setInput(getKey<InputBase>("control"), this.control);
    componentRef.setInput(getKey<InputBase>("htmlIdPrefix"), this.htmlIdPrefix);
    componentRef.setInput(
      getKey<InputBase>("notifications$"),
      this.notifications$,
    );

    componentRef.instance.focusChange
      .pipe(untilDestroyed(this))
      .subscribe((focusChangeEvent) => {
        this.focusChange.emit(focusChangeEvent);
      });
  }

  getLabelClasses() {
    // Note: This was too complex and could not be solved via the ngClass from Angular
    let labelClasses = "pt-1 flex flex-row flex-wrap";
    if (this.field.isLabelHidden) {
      labelClasses += " sr-only";
    }
    if (this.field.labelClassName) {
      labelClasses += ` ${this.field.labelClassName}`;
    }
    return labelClasses;
  }
}

type CustomValidationErrors = {
  [key in ValidationErrorKey]: Record<string, number>;
};
